import { Updater } from '@tanstack/react-table';
import { ControllerHelper, FetchPostsFilter, PALMSController, PALMSUserPrograms, PlaylistDownloadDto, PlaylistStateDownloadDto, PlaylistUploadDto, PostController } from 'collaboration-service';
import InfoBox from 'components/InfoBox/InfoBox';
import Pagination from 'components/Pagination/Pagination';
import { Button, Checkbox, Icon, ImgSelect, Input, Loader, aboveBreakpoint, aboveBreakpointOrEqual, belowBreakpointOrEqual, useThemePart } from 'imaginarity-react-ui';
import { rootElement } from 'index';
import { FloatedRight } from 'layout/masterLayouts/MasterLayoutsSC';
import _ from 'lodash';
import Carousel from 'nuka-carousel';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { WorkingActions } from 'services/ApplicationState/Actions';
import { ApplicationState, useAppDispatch } from 'services/ApplicationState/ApplicationState';
import { countNodeItemsToBeDonePerNode, getCriticalAndPendingCounts, getPlaylistStateDueDate, getProgressStatus, reducerBatchJob, reducerSetIfChanged, reducerUpdater, shallowCompare } from 'services/Helpers';
import { gotoNamedRouteMethod } from 'services/Helpers/RoutingHelper';
import { getTranslated } from 'services/Helpers/TranslationHelpers';
import History from 'services/History';
import { useImgI18N } from 'services/ImgI18N';
import { isAdmin } from 'services/StoreDependantHelpers';
import { ThemeContext } from 'styled-components';
import { getDateString, getSlideProperties } from 'views/MyHome/myHomeHelpers';
import { MyHomeStartSC } from 'views/MyHome/MyHomeStartSC';
import PlaylistOverviewCarouselItem from './PlaylistOverviewCarouselItem';
import PlaylistOverviewItem from './PlaylistOverviewItem';
import PlaylistOverviewLPItem from './PlaylistOverviewLPItem';
import { PlaylistSC as T } from './PlaylistSC';
import { getPlaylistUserRoles } from './Edit/PLaylistEditMain';

const getMaxValue = (numbers: number[]): number => {
    return Math.max(...numbers);
};

interface ITS {
    value: number;
    label: string;
}

const itemsToShow: ITS[] = [
    { value: 5, label: "5" },
    { value: 10, label: "10" },
    { value: 20, label: "20" },
    { value: 50, label: "50" },
];

const mapper = (state: ApplicationState) => ({
    playlistStatesTopic: state.playlistsState.playlistStatesTopic,
    contentLanguage: state.contentLanguage,
    params: state.params,
    currentGroup: state.currentGroup,
});

interface PlaylistOverviewProps {
}

type SortBy = "assigned" | "name" | "duedate" | "duration" | "progress";
// Define the state type
interface PlaylistOverviewState {
    showBtns: boolean;
    showCarousel: boolean;
    sortedLists: {
        undone: PlaylistStateDownloadDto[];
        done: PlaylistStateDownloadDto[];
        optional: PlaylistStateDownloadDto[];
        undoneStarted: PlaylistStateDownloadDto[];
    };
    sortBy: SortBy;
    reverseSort: boolean;
    ppnId?: string;
    searchTerm: string;
    palmsPrograms: PALMSUserPrograms[];
    maxElements: number;
    pageCountPalms: number;
    pageCountUndone: number;
    pageCountOptional: number;
    headlineTopTitle: string;
    pageCountDone: number;
    itemsPerPage?: ITS;
    isLoading: boolean;
}

// Define the action type
type PlaylistOverviewAction =
    | { type: "BATCH"; payload: PlaylistOverviewAction[] }
    | { type: "SET_SHOW_BTNS"; payload: boolean }
    | { type: "SET_SHOW_CAROUSEL"; payload: boolean }
    | { type: "SET_SORTED_LISTS"; payload: Updater<PlaylistOverviewState["sortedLists"]> }
    | { type: "SET_SORT_BY"; payload: SortBy }
    | { type: "SET_REVERSE_SORT"; payload: boolean }
    | { type: "SET_PPN_ID"; payload?: string }
    | { type: "SET_SEARCH_TERM"; payload: string }
    | { type: "SET_PALMS_PROGRAMS"; payload: PALMSUserPrograms[] }
    | { type: "SET_MAX_ELEMENTS"; payload: number }
    | { type: "SET_PAGE_COUNT_PALMS"; payload: number }
    | { type: "SET_PAGE_COUNT_UNDONE"; payload: number }
    | { type: "SET_PAGE_COUNT_OPTIONAL"; payload: number }
    | { type: "SET_HEADLINE_TOP_TITLE"; payload: string }
    | { type: "SET_PAGE_COUNT_DONE"; payload: number }
    | { type: "SET_ITEMS_PER_PAGE"; payload?: ITS }
    | { type: "SET_IS_LOADING"; payload: boolean };

// Helper functions for actions
const batch = (payload: PlaylistOverviewAction[]): PlaylistOverviewAction => ({ type: "BATCH", payload });
const setShowBtns = (payload: boolean): PlaylistOverviewAction => ({ type: "SET_SHOW_BTNS", payload });
const setShowCarousel = (payload: boolean): PlaylistOverviewAction => ({ type: "SET_SHOW_CAROUSEL", payload });
const setSortedLists = (payload: Updater<PlaylistOverviewState["sortedLists"]>): PlaylistOverviewAction => ({ type: "SET_SORTED_LISTS", payload });
const setSortBy = (payload: SortBy): PlaylistOverviewAction => ({ type: "SET_SORT_BY", payload });
const setReverseSort = (payload: boolean): PlaylistOverviewAction => ({ type: "SET_REVERSE_SORT", payload });
const setPpnId = (payload: string): PlaylistOverviewAction => ({ type: "SET_PPN_ID", payload });
const setSearchTerm = (payload: string): PlaylistOverviewAction => ({ type: "SET_SEARCH_TERM", payload });
const setPalmsPrograms = (payload: PALMSUserPrograms[]): PlaylistOverviewAction => ({ type: "SET_PALMS_PROGRAMS", payload });
const setMaxElements = (payload: number): PlaylistOverviewAction => ({ type: "SET_MAX_ELEMENTS", payload });
const setPageCountPalms = (payload: number): PlaylistOverviewAction => ({ type: "SET_PAGE_COUNT_PALMS", payload });
const setPageCountUndone = (payload: number): PlaylistOverviewAction => ({ type: "SET_PAGE_COUNT_UNDONE", payload });
const setPageCountOptional = (payload: number): PlaylistOverviewAction => ({ type: "SET_PAGE_COUNT_OPTIONAL", payload });
const setHeadlineTopTitle = (payload: string): PlaylistOverviewAction => ({ type: "SET_HEADLINE_TOP_TITLE", payload });
const setPageCountDone = (payload: number): PlaylistOverviewAction => ({ type: "SET_PAGE_COUNT_DONE", payload });
const setItemsPerPage = (payload?: ITS): PlaylistOverviewAction => ({ type: "SET_ITEMS_PER_PAGE", payload });
const setIsLoading = (payload: boolean): PlaylistOverviewAction => ({ type: "SET_IS_LOADING", payload });

// Reducer function
const reducer = (state: PlaylistOverviewState, action: PlaylistOverviewAction): PlaylistOverviewState => {
    switch (action.type) {
        case "BATCH":
            return reducerBatchJob(state, action.payload, reducer);
        case "SET_SHOW_BTNS":
            return reducerSetIfChanged(state, "showBtns", action.payload);
        case "SET_SHOW_CAROUSEL":
            return reducerSetIfChanged(state, "showCarousel", action.payload);
        case "SET_SORTED_LISTS":
            return reducerUpdater(state, "sortedLists", action.payload);
        case "SET_SORT_BY":
            return reducerSetIfChanged(state, "sortBy", action.payload);
        case "SET_REVERSE_SORT":
            return reducerSetIfChanged(state, "reverseSort", action.payload);
        case "SET_PPN_ID":
            return reducerSetIfChanged(state, "ppnId", action.payload);
        case "SET_SEARCH_TERM":
            return reducerSetIfChanged(state, "searchTerm", action.payload);
        case "SET_PALMS_PROGRAMS":
            return reducerSetIfChanged(state, "palmsPrograms", action.payload);
        case "SET_MAX_ELEMENTS":
            return reducerSetIfChanged(state, "maxElements", action.payload);
        case "SET_PAGE_COUNT_PALMS":
            return reducerSetIfChanged(state, "pageCountPalms", action.payload);
        case "SET_PAGE_COUNT_UNDONE":
            return reducerSetIfChanged(state, "pageCountUndone", action.payload);
        case "SET_PAGE_COUNT_OPTIONAL":
            return reducerSetIfChanged(state, "pageCountOptional", action.payload);
        case "SET_HEADLINE_TOP_TITLE":
            return reducerSetIfChanged(state, "headlineTopTitle", action.payload);
        case "SET_PAGE_COUNT_DONE":
            return reducerSetIfChanged(state, "pageCountDone", action.payload);
        case "SET_ITEMS_PER_PAGE":
            return reducerSetIfChanged(state, "itemsPerPage", action.payload);
        case "SET_IS_LOADING":
            return reducerSetIfChanged(state, "isLoading", action.payload);
        default:
            return state;
    }
};

const initialState: PlaylistOverviewState = {
    showBtns: false,
    showCarousel: false,
    sortedLists: { undone: [], done: [], optional: [], undoneStarted: [] },
    sortBy: "duedate",
    reverseSort: false,
    ppnId: undefined,
    searchTerm: "",
    palmsPrograms: [],
    maxElements: 0,
    pageCountPalms: 0,
    pageCountUndone: 0,
    pageCountOptional: 0,
    headlineTopTitle: "all trainings",
    pageCountDone: 0,
    itemsPerPage: undefined,
    isLoading: false,
};

const PlaylistOverview = (p: PlaylistOverviewProps) => {
    const { playlistStatesTopic, contentLanguage, params, currentGroup } = useSelector(mapper, shallowCompare);
    const filter = params?.filter;


    const dispatch = useAppDispatch();
    const { t } = useImgI18N("myhomelearner");
    const contentWidth = useThemePart(x => x.content.width);
    const { slidesToShow, slidesGap, slideWidth } = getSlideProperties(contentWidth);
    const [state, localDispatch] = React.useReducer(reducer, initialState);
    const {
        showBtns,
        showCarousel,
        sortedLists,
        sortBy,
        reverseSort,
        ppnId,
        searchTerm,
        palmsPrograms,
        maxElements,
        pageCountPalms,
        pageCountUndone,
        pageCountOptional,
        headlineTopTitle,
        pageCountDone,
        itemsPerPage,
        isLoading,
    } = state;
    const tdy = getDateString(new Date());
    const carHeight = React.useMemo(() => {
        return Math.ceil(((contentWidth - ((slidesToShow - 1) * slidesGap)) / 4) * 9 / 16) + 69 + 65;
    }, [contentWidth, slidesToShow, slidesGap]);

    // Optimierung der Memoization und der Abhängigkeiten
    const undoneStartedSortedByDate = React.useMemo(() => {
        return [...sortedLists.undoneStarted].sort((a, b) =>
            new Date(b.playlist.changed).getTime() - new Date(a.playlist.changed).getTime()
        );
    }, [sortedLists.undoneStarted]);

    const listLinear = _.filter(sortedLists.undone, f => (f.playlist as PlaylistUploadDto).sequencingMode === "Linear");
    const hasLearningPaths = palmsPrograms.length > 0;
    const hasMandatoryEntries = sortedLists.undone.length > 0;
    const hasOptionalEntries = sortedLists.optional.length > 0;
    const hasDoneEntries = sortedLists.done.length > 0;

    const { playlists, subPlaylists } = React.useMemo(() => {
        const x = _.partition(playlistStatesTopic, p => !p.parentStateId);
        return {
            playlists: x[0],
            subPlaylists: x[1]
        }
    }, [playlistStatesTopic]);


    const loadData = React.useCallback(async () => {
        const darray: string[] = [tdy];
        const data = await ControllerHelper.singleCall({ dateString: darray.toString(), ppnId: "", onlyPalms: true }, PALMSController.LearningPaths, true);

        let dataToSet = data?.programs?.filter(x => x?.origin === "PALMS") ?? [];
        if (searchTerm !== "") {
            const searchLower = searchTerm.toLowerCase();
            dataToSet = dataToSet.filter(p => p.id.toLowerCase().includes(searchLower) || p.title?.toLowerCase().includes(searchLower));
        }
        localDispatch(setPalmsPrograms(reverseSort ? _.reverse(dataToSet) : dataToSet));
        localDispatch(setPpnId(data?.ppnId));
    }, [tdy, searchTerm, reverseSort]);




    React.useEffect(() => {
        let x: NodeJS.Timeout | undefined = undefined;
        const redo = () => {
            dispatch(WorkingActions.WIKI.loadPlaylistState());
            x = setTimeout(redo, 1000 * 30);
        }
        redo();
        return () => {
            if (x) clearTimeout(x);
        }
    }, [dispatch]);

    const loading = isLoading || (playlistStatesTopic === undefined);

    React.useEffect(() => {
        if (filter === "optional") {
            localDispatch(setHeadlineTopTitle(("optional trainings")));
        }
        else if (filter === "mandatory") {
            loadData();
            localDispatch(setHeadlineTopTitle(("mandatory trainings")));
        }
        else {
            localDispatch(setHeadlineTopTitle(("all trainings")));
        }
    }, [filter, loadData]);

    React.useEffect(() => {
        if (filter === "optional") {
            const v = [sortedLists.optional.length, sortedLists.done.length];
            localDispatch(setMaxElements(getMaxValue(v)));
        }
        else if (filter === "mandatory") {
            const v = [sortedLists.undone.length, sortedLists.done.length, palmsPrograms.length];
            localDispatch(setMaxElements(getMaxValue(v)));
        }
        else {
            const v = [sortedLists.undone.length, sortedLists.done.length, palmsPrograms.length, sortedLists.optional.length, sortedLists.done.length];
            localDispatch(setMaxElements(getMaxValue(v)));
        }
    }, [filter, palmsPrograms.length, sortedLists.done.length, sortedLists.optional.length, sortedLists.undone.length]);

    const methodSetIsLoading = React.useCallback((loading: boolean) => {
        localDispatch(setIsLoading(loading));
    }, [localDispatch]);

    const filteredPlaylistStatesTopic = React.useMemo(() => {
        if (filter === "optional") return playlists?.filter(p => p.optional === true) ?? [];
        if (filter === "mandatory") return playlists?.filter(p => p.optional === false) ?? [];
        return playlists ?? [];
    }, [playlists, filter]);

    const filteredLists = React.useMemo(() => {
        const searchLower = searchTerm.toLowerCase();
        const filteredNotDone = _.filter(filteredPlaylistStatesTopic, p =>
            p.state !== "Finished" &&
            p.optional === false &&
            p.playlist.headlines.some(headline => headline.text.toLowerCase().includes(searchLower))
        );
        const filterDone = _.filter(filteredPlaylistStatesTopic,
            p => p.state === "Finished" &&
                p.playlist.headlines.some(headline => headline.text.toLowerCase().includes(searchLower))
        );
        const filteredOptional = _.filter(filteredPlaylistStatesTopic,
            p => p.state !== "Finished" &&
                p.optional === true &&
                p.playlist.headlines.some(headline => headline.text.toLowerCase().includes(searchLower))
        );
        const filteredNotDoneStarted = _.filter(filteredPlaylistStatesTopic, p => {
            let criticalCount = 0;
            criticalCount += countNodeItemsToBeDonePerNode(p, p.curNodes);
            criticalCount += countNodeItemsToBeDonePerNode(p, p.prevNodes);
            criticalCount += countNodeItemsToBeDonePerNode(p, p.nextNodes);

            return (
                p.state !== "Finished" &&
                p.optional === false &&
                criticalCount > 0 &&
                p.playlist.headlines.some(headline => headline.text.toLowerCase().includes(searchLower))
            );
        });
        return [filteredNotDone, filterDone, filteredOptional, filteredNotDoneStarted]
    }, [filteredPlaylistStatesTopic, searchTerm]);

    const [hasPlaylistUserRole, setHasPlaylistUserRole] = React.useState(false);

    React.useEffect(() => {
        const checkPlaylistRoles = async () => {
            if (!currentGroup) return;
            const f: FetchPostsFilter = {
                includedPostTypes: ["Playlist"],
                onlyInGroups: [currentGroup.id],
            };
            const res = await ControllerHelper.singleCall({ token: { token: "" }, filter: f, loadingCount: 100 }, PostController.GetPagedForAllGroups);
            if (res?.elements) {
                const hasRole = res.elements.some((playlist) => {
                    const roles = getPlaylistUserRoles(playlist as PlaylistDownloadDto);
                    return roles.assign || roles.delete || roles.update || roles.create;
                });
                setHasPlaylistUserRole(hasRole);
            }
        };
        checkPlaylistRoles();
    }, [currentGroup]);


    React.useEffect(() => {
        const [filteredNotDone, filterDone, filteredOptional, filteredNotDoneStarted] = filteredLists;

        let sortFunctionPlaylist: _.ListIteratee<PlaylistStateDownloadDto> = () => 0;

        switch (sortBy) {
            case 'assigned':
                sortFunctionPlaylist = (p: PlaylistStateDownloadDto) => p.assignmentDate;
                break;
            case 'name':
                sortFunctionPlaylist = (p: PlaylistStateDownloadDto) => getTranslated(p.playlist.headlines, contentLanguage)?.text ?? "";
                break;
            case 'duedate':
                sortFunctionPlaylist = getPlaylistStateDueDate;
                break;
            case 'progress':
                sortFunctionPlaylist = (p: PlaylistStateDownloadDto) => {
                    const curNode = p.curNodes[0];
                    const progressStatus = getProgressStatus(curNode, p);
                    const toBeDoneResult = getCriticalAndPendingCounts(p);
                    const x = toBeDoneResult.criticalCount;

                    switch (progressStatus) {
                        case "done":
                            return 0 + x;
                        case "overdue":
                            return 100 + x;
                        case "warning":
                            return 200 + x;
                        case "on Track":
                            return 300 + x;
                    }
                };
                break;
        }

        const sortedPlaylistData = {
            undone: _.sortBy(filteredNotDone, sortFunctionPlaylist),
            done: _.sortBy(filterDone, sortFunctionPlaylist),
            optional: _.sortBy(filteredOptional, sortFunctionPlaylist),
            undoneStarted: _.sortBy(filteredNotDoneStarted, sortFunctionPlaylist),
        };


        localDispatch(setSortedLists(prev => {
            const newSortedLists = reverseSort ? {
                undone: _.reverse(sortedPlaylistData.undone),
                done: _.reverse(sortedPlaylistData.done),
                optional: _.reverse(sortedPlaylistData.optional),
                undoneStarted: _.reverse(sortedPlaylistData.undoneStarted),
            } : sortedPlaylistData;
            return _.isEqual(prev, newSortedLists) ? prev : newSortedLists;
        }));

    }, [filteredLists, sortBy, reverseSort, contentLanguage, palmsPrograms]);

    const theme = React.useContext(ThemeContext);
    const isMobile = belowBreakpointOrEqual({ theme }, "largeMobile");
    const isTablet = aboveBreakpoint({ theme }, "largeMobile");
    const isLaptop = aboveBreakpointOrEqual({ theme }, "tablet");
    const isDesktop = aboveBreakpoint({ theme }, "desktop");
    const adminWiki = isAdmin("WIKI");
    const adminPlaylist = isAdmin("PLAYLIST");
    const admin = adminWiki || adminPlaylist;
    const showDescs = contentWidth > 800;



    const setSort = React.useCallback((by: SortBy) => () => {
        localDispatch(setSortBy(by));
        localDispatch(setReverseSort(by === sortBy ? !reverseSort : false));
    }, [sortBy, reverseSort]);

    const getVisibleItems = React.useCallback(
        (list: PlaylistStateDownloadDto[], pageCount: number) => {
            if (!itemsPerPage) return list;
            const perPage = itemsPerPage.value;
            const startIndex = pageCount * perPage;
            const endIndex = startIndex + perPage;
            return list.slice(startIndex, endIndex);
        },
        [itemsPerPage]
    );
    const getVisiblePalmsItems = React.useCallback(
        (list: PALMSUserPrograms[], pageCount: number) => {
            if (!itemsPerPage) return list;
            const perPage = itemsPerPage.value;
            const startIndex = pageCount * perPage;
            const endIndex = startIndex + perPage;
            return list.slice(startIndex, endIndex);
        },
        [itemsPerPage]
    );

    const visibleItems = React.useMemo(() => ({
        visibleUndone: getVisibleItems(sortedLists.undone, pageCountUndone),
        visibleOptional: getVisibleItems(sortedLists.optional, pageCountOptional),
        visibleDone: getVisibleItems(sortedLists.done, pageCountDone),
        visiblePalmsPrograms: getVisiblePalmsItems(palmsPrograms, pageCountPalms),
    }), [sortedLists, pageCountUndone, pageCountOptional, pageCountDone, pageCountPalms, getVisibleItems, getVisiblePalmsItems, palmsPrograms]);


    const filteredItemsToShow = itemsToShow.filter(item => item.value <= maxElements + 10);



    const paginationCallbacks = React.useMemo(() => ({
        paginateUndone: (i: number) => localDispatch(setPageCountUndone(i)),
        paginateOptional: (i: number) => localDispatch(setPageCountOptional(i)),
        paginateDone: (i: number) => localDispatch(setPageCountDone(i)),
        paginatePalms: (i: number) => localDispatch(setPageCountPalms(i)),
    }), []);

    const hasResults = sortedLists.undoneStarted.length > 0 || sortedLists.done.length > 0 || sortedLists.optional.length > 0 || sortedLists.undone.length > 0 || palmsPrograms.length > 0;

    const showItemsToShowDropdown = React.useMemo(() => {
        return (sortedLists.undone.length > 5 || sortedLists.optional.length > 5 || sortedLists.done.length > 5 || palmsPrograms.length > 5 || searchTerm !== "");
    }, [sortedLists, palmsPrograms.length, searchTerm]);



    return (
        <T.POOuter isMobile={isMobile}>
            <MyHomeStartSC.MHLPOuterContainerBackgroundImage imageUrl='url("https://cdn.imaginarity.com/public/myhome/new/images/playlist/playlistHeader.jpg")' style={{ backgroundPosition: "right top" }} />
            <T.HeadlineTop isMobile={isMobile}>
                <T.HeadlineTopTitle>
                    {t(headlineTopTitle)}
                </T.HeadlineTopTitle>
                {showItemsToShowDropdown &&
                    <>
                        <T.HeadlineTopText isMobile={isMobile}>
                            {t("trainings to be displayed")}:
                        </T.HeadlineTopText>
                        <T.HeadlineTopTextContainer isMobile={isMobile}>
                            <ImgSelect
                                options={filteredItemsToShow}
                                placeholder={t("all")}
                                menuPortalTarget={rootElement}
                                value={itemsPerPage ?? null}
                                onChange={(selectedOption) => {
                                    const actions = [
                                        setPageCountUndone(0),
                                        setPageCountOptional(0),
                                        setPageCountDone(0),
                                    ];
                                    if (selectedOption) {
                                        actions.push(setItemsPerPage(selectedOption));
                                    }
                                    localDispatch(batch(actions));
                                }}
                            />

                        </T.HeadlineTopTextContainer>
                    </>
                }
                <T.HeadlineTopTextContainer isMobile={isMobile}>
                    {itemsPerPage !== undefined && (
                        <Button
                            icon="times"
                            kind='secondary'
                            onClick={() => {
                                localDispatch(batch([
                                    setItemsPerPage(undefined),
                                    setPageCountUndone(0),
                                    setPageCountOptional(0),
                                    setPageCountDone(0),
                                ]));
                            }}
                            tooltip={{ tooltipText: t("reset pagination") }}
                        />

                    )}

                </T.HeadlineTopTextContainer>
                <T.GRS41 isMobile={isMobile}>
                    <T.HeadlineInput>
                        <FloatedRight>
                            <Input onChange={e => localDispatch(setSearchTerm(e.target.value))}
                                width={isMobile ? "100%" : 200}
                                value={searchTerm}
                                autoComplete='off'
                                placeholder={t(isMobile ? "search" : "search by keyword...")}
                                icon='search'
                                type='search'
                                kind={searchTerm === "" ? 'default' : hasResults ? 'bottomLine' : 'bottomLineError'}
                            />
                        </FloatedRight>
                        {searchTerm !== "" &&
                            <T.HeadlineInputClear onClick={() => localDispatch(setSearchTerm(""))}>
                                <Icon name='times' color='@lightGrey' marginTop={0} />
                            </T.HeadlineInputClear>
                        }
                    </T.HeadlineInput>
                </T.GRS41>
                <T.GRS41P5 isMobile={isMobile}>
                    {(admin || hasPlaylistUserRole) &&
                        <Button
                            icon="playlist edit"
                            kind="primary"
                            onClick={gotoNamedRouteMethod("playlist_manage")}
                            tooltip={{ tooltipText: t("manage playlist elements") }}
                            floated='right'
                        />
                    }
                    <Button
                        icon="chevron left"
                        kind="halfTransparentButton"
                        onClick={() => History.goBack()}
                        tooltip={{ tooltipText: t("back") }}
                        floated='right'
                    />
                    {/* {filter !== "optional" && */}
                    {undoneStartedSortedByDate.length > 0 &&

                        <FloatedRight margin='0 5px 0 0'>
                            <Button
                                icon="carousel"
                                kind="halfTransparentButton"
                                onClick={() => localDispatch(setShowCarousel(!showCarousel))}
                                tooltip={{ tooltipText: t(showCarousel ? "hide your already processed playlists" : "show your already processed playlists") }}
                                floated='right'
                                iconColor={showCarousel ? "@accentRed" : undefined}
                            />
                        </FloatedRight>
                    }

                </T.GRS41P5>
            </T.HeadlineTop>
            {loading ? <Loader active infoText={t("loading playlists")} size='small' light /> :
                <>
                    {hasResults ?
                        <>
                            {hasMandatoryEntries && sortedLists.undoneStarted.length > 0 &&
                                <T.CarouselOuterContainer
                                    hasMandatoryEntries={hasMandatoryEntries}
                                    showCarousel={showCarousel}
                                    carHeight={carHeight}
                                    sortedLists={sortedLists}
                                >
                                    <T.Headline mT={30}>
                                        {searchTerm === "" ? t("my already processed playlists") : t("my filtered playlists")}
                                    </T.Headline>
                                    <T.CarouselContainer>
                                        <Carousel
                                            slidesToShow={slidesToShow}
                                            dragging={listLinear.length > slidesToShow}
                                            slideWidth={slideWidth}
                                            cellSpacing={slidesGap}
                                            slidesToScroll={slidesToShow}
                                            renderCenterRightControls={null}
                                            renderCenterLeftControls={null}
                                            renderBottomCenterControls={null}
                                            renderTopRightControls={slidesToShow >= sortedLists.undone.length ? null : ({ previousSlide, nextSlide, currentSlide, slideCount }) => (listLinear.length > slidesToShow &&
                                                <>
                                                    {contentWidth >= 480 &&
                                                        <T.CarouselButtonLeft onClick={previousSlide} disabled={currentSlide === 0}>
                                                            <Icon name="chevron left" size="22px" />
                                                        </T.CarouselButtonLeft>
                                                    }
                                                    {contentWidth >= 480 &&
                                                        <T.CarouselButtonRight onClick={nextSlide} disabled={slideCount === currentSlide + slidesToShow}>
                                                            <Icon name="chevron right" size="22px" />
                                                        </T.CarouselButtonRight>
                                                    }

                                                </>
                                            )}
                                        >
                                            {_.map(undoneStartedSortedByDate, (p, i) => {
                                                const isAAO = p.curNodes.length === 0;
                                                return (
                                                    <PlaylistOverviewCarouselItem data={p} isAAO={isAAO} key={i} />
                                                );
                                            })}
                                        </Carousel>
                                    </T.CarouselContainer>
                                </T.CarouselOuterContainer>
                            }

                            {/* <T.Headline>
                                {filter ? "" : (searchTerm === "" ? t("all playlists") : t('all playlists filtered by "{{search}}"', { search: searchTerm }))}
                            </T.Headline> */}

                            <T.TableGrid showButtons={showBtns} >
                                <T.TitleRowCell cursor={"default"} />
                                <T.TitleRowCell cursor={"default"} padding="0px"  >
                                    <Icon name="image" />
                                </T.TitleRowCell>
                                <T.TitleRowCell onClick={setSort("name")}>
                                    <div>{t("name")}</div>
                                    {sortBy === "name" ?
                                        <div><Icon name={reverseSort ? "arrow down" : "arrow up"} style={{ float: "left", marginLeft: 5, }} size="14px" marginTop={3} /></div>
                                        : <div />}
                                </T.TitleRowCell>
                                {showDescs ? <T.TitleRowCell cursor={"default"}>
                                    <div>{t("description")}</div>
                                </T.TitleRowCell> : <div />}
                                {isDesktop ? <T.TitleRowCell onClick={setSort("assigned")}>
                                    <div>{t("assigned")}</div>
                                    {sortBy === "assigned" ?
                                        <div><Icon name={reverseSort ? "arrow down" : "arrow up"} style={{ float: "left", marginLeft: 5, }} size="14px" marginTop={3} /></div>
                                        : <div />}
                                </T.TitleRowCell> : <div />}
                                {isTablet && (filter !== "optional") ? <T.TitleRowCell onClick={setSort("duedate")}>
                                    <div>{t("due date")}</div>
                                    {sortBy === "duedate" ?
                                        <div><Icon name={reverseSort ? "arrow down" : "arrow up"} style={{ float: "left", marginLeft: 5, }} size="14px" marginTop={3} /></div>
                                        : <div />}
                                </T.TitleRowCell> : <div />}
                                {isLaptop ? <T.TitleRowCell onClick={setSort("duration")}>
                                    <div>{t("duration")}</div>
                                    {sortBy === "duration" ?
                                        <div><Icon name={reverseSort ? "arrow down" : "arrow up"} style={{ float: "left", marginLeft: 5, }} size="14px" marginTop={3} /></div>
                                        : <div />}
                                </T.TitleRowCell> : <div />}
                                <T.TitleRowCell onClick={setSort("progress")} >
                                    <div>{t("completion")}</div>
                                    {sortBy === "progress" ?
                                        <div><Icon name={reverseSort ? "arrow down" : "arrow up"} style={{ float: "left", marginLeft: 5, }} size="14px" marginTop={3} /></div>
                                        : <div />}
                                </T.TitleRowCell>
                                {showBtns && <T.TitleRowCell cursor={"default"}>actions</T.TitleRowCell>}
                                <T.TitleRowCell cursor={"default"} />

                                {hasLearningPaths &&
                                    <>
                                        <T.HeadlineRow gcs={1} gce={8} top={0}>
                                            {t("my learningPaths")}
                                        </T.HeadlineRow>
                                        <T.HeadlineRowInfo gcs={8} gce={showBtns ? 11 : 10} top={0}>
                                            <Icon name="more vertical" style={{ float: "left", marginRight: 7, marginLeft: 4 }} color={'@middleLightGrey'} />
                                            {t("{{count}} learning paths", { count: palmsPrograms.length })}
                                        </T.HeadlineRowInfo>
                                        {_.map(visibleItems.visiblePalmsPrograms, (p, i) => {
                                            return (
                                                <div className="rowWrapper" key={p.id}>
                                                    <PlaylistOverviewLPItem inputData={p} ppnUserId={ppnId ?? ""} imgNbr={i} showActions={showBtns} />
                                                </div>
                                            )
                                        })}
                                        {itemsPerPage && palmsPrograms.length > itemsPerPage.value &&
                                            <T.PaginationRow showBtns={showBtns}>
                                                <Pagination
                                                    count={Math.ceil(palmsPrograms.length / itemsPerPage.value)}
                                                    initialIndex={pageCountPalms}
                                                    paginate={paginationCallbacks.paginatePalms}
                                                />
                                            </T.PaginationRow>
                                        }
                                    </>
                                }
                                {hasMandatoryEntries &&
                                    <>
                                        <T.HeadlineRow gcs={1} gce={8} top={0}>
                                            {t("my mandatory playlists")}
                                        </T.HeadlineRow>
                                        <T.HeadlineRowInfo gcs={8} gce={showBtns ? 11 : 10} top={0}>
                                            <Icon name="more vertical" style={{ float: "left", marginRight: 7, marginLeft: 4 }} color={'@middleLightGrey'} />
                                            {t("{{count}} playlists", { count: sortedLists.undone.length })}
                                        </T.HeadlineRowInfo>
                                        {_.map(visibleItems.visibleUndone, (p) => (
                                            <div className="rowWrapper" key={p.id}>
                                                <PlaylistOverviewItem data={p} sub={subPlaylists} showBtns={showBtns} progress={getProgressStatus(p.curNodes[0], p)} setIsLoading={methodSetIsLoading} />
                                            </div>
                                        ))}
                                        {itemsPerPage && sortedLists.undone.length > itemsPerPage.value &&
                                            <T.PaginationRow showBtns={showBtns}>
                                                <Pagination
                                                    count={Math.ceil(sortedLists.undone.length / itemsPerPage.value)}
                                                    initialIndex={pageCountUndone}
                                                    paginate={paginationCallbacks.paginateUndone}
                                                />
                                            </T.PaginationRow>
                                        }
                                    </>
                                }
                                {hasOptionalEntries &&
                                    <>
                                        <T.HeadlineRow gcs={1} gce={8} top={40}>
                                            {t("my optional playlists")}
                                        </T.HeadlineRow>
                                        <T.HeadlineRowInfo gcs={8} gce={showBtns ? 11 : 10} top={40}>
                                            <Icon name="more vertical" style={{ float: "left", marginRight: 7, marginLeft: 4 }} color={'@middleLightGrey'} />
                                            {t("{{count}} playlists", { count: sortedLists.optional.length })}
                                        </T.HeadlineRowInfo>
                                        {_.map(visibleItems.visibleOptional, (p) => (
                                            <div className="rowWrapper" key={p.id}>
                                                <PlaylistOverviewItem data={p} sub={subPlaylists} showBtns={showBtns} progress="on Track" setIsLoading={methodSetIsLoading} />
                                            </div>
                                        ))}
                                        {itemsPerPage && sortedLists.optional.length > itemsPerPage.value &&
                                            <T.PaginationRow showBtns={showBtns}>
                                                <Pagination
                                                    count={Math.ceil(sortedLists.optional.length / itemsPerPage.value)}
                                                    initialIndex={pageCountOptional}
                                                    paginate={paginationCallbacks.paginateOptional}
                                                />
                                            </T.PaginationRow>
                                        }
                                    </>
                                }
                                {hasDoneEntries &&
                                    <>
                                        <T.HeadlineRow gcs={1} gce={8} top={80}>
                                            {t("my completed playlists")}
                                        </T.HeadlineRow>
                                        <T.HeadlineRowInfo gcs={8} gce={showBtns ? 11 : 10} done={true} top={80}>
                                            <Icon name="check thin" style={{ float: "left", marginRight: 7, marginLeft: 4 }} color={'@accentGreen'} />
                                            {t("{{count}} playlists", { count: sortedLists.done.length })}
                                        </T.HeadlineRowInfo>
                                        {_.map(visibleItems.visibleDone, (p) => (
                                            <div className="rowWrapper" key={p.id}>
                                                <PlaylistOverviewItem data={p} sub={subPlaylists} showBtns={showBtns} progress="done" setIsLoading={methodSetIsLoading} />
                                            </div>
                                        ))}
                                        {itemsPerPage && sortedLists.done.length > itemsPerPage.value &&

                                            <T.PaginationRow showBtns={showBtns}>
                                                <Pagination
                                                    count={Math.ceil(sortedLists.done.length / itemsPerPage.value)}
                                                    initialIndex={pageCountDone}
                                                    paginate={paginationCallbacks.paginateDone}
                                                />
                                            </T.PaginationRow>
                                        }
                                    </>
                                }
                            </T.TableGrid>

                        </>
                        : <T.MT40>
                            <InfoBox
                                content={
                                    searchTerm === "" ?
                                        t("no playlists found") :
                                        t("no playlists with the term {{searchword}} in the title were found", { searchword: searchTerm })
                                }
                                icon='exclamation circle'
                                noBold
                                noLetterSpacing
                                noUppercase
                                onClick={() => localDispatch(setSearchTerm(""))}
                                type='alert'
                                closeable
                            />
                        </T.MT40>
                    }
                </>
            }

            {
                admin &&
                <T.AdminPanel >
                    <div style={{ float: "left", lineHeight: "40px", paddingLeft: 20 }}>Temporary Admin Panel</div>
                    <div style={{ float: "right", borderLeft: "1px solid #e9e9e9", width: 1, height: "100%", marginRight: 10 }} />
                    <div style={{ float: "right", lineHeight: "20px", marginRight: 10, marginTop: 9, cursor: "pointer" }} onClick={() => localDispatch(setShowBtns(!showBtns))}>
                        Reset  Buttons
                    </div>
                    <div style={{ float: "right", lineHeight: "20px", marginRight: 10, marginTop: 7 }}>
                        <Checkbox label='hide' onClick={() => !showBtns ? undefined : localDispatch(setShowBtns(false))} selected={!showBtns} />
                    </div>
                    <div style={{ float: "right", lineHeight: "20px", marginRight: 10, marginTop: 7 }}>
                        <Checkbox label='show' onClick={() => showBtns ? undefined : localDispatch(setShowBtns(true))} selected={showBtns} />
                    </div>
                    <div style={{ float: "right", borderLeft: "1px solid #e9e9e9", width: 1, height: "100%", marginRight: 10 }} />
                </T.AdminPanel>
            }

        </T.POOuter >
    );
}
export default PlaylistOverview;